import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  CheckCircle2,
  XCircle,
  Shield,
  Database,
  KeyRound,
  ArrowRightLeft,
  AlertTriangle,
  LogIn,
  Settings,
  Filter,
} from 'lucide-react';
import { AuditLogEmpty } from '@/components/illustrations/AuditLogEmpty';
import { apiGet, apiDownloadPost } from '@/api/client';
import { EntitlementGate } from '@/components/billing/EntitlementGate';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatRelativeDate } from '@/lib/formatters';
import type { AuditEventType } from '@/types/audit';

interface AuditEntry {
  id: string;
  event_type: AuditEventType;
  actor_id: string | null;
  actor_name: string;
  principal_id: string | null;
  details: string;
  ip_address: string | null;
  resource_ids: string[];
  timestamp: string;
}

interface AuditResponse {
  data: AuditEntry[];
  meta: { pagination: { page: number; per_page: number; total: number; total_pages: number } };
}

const EVENT_CONFIG: Record<string, { icon: typeof CheckCircle2; label: string; color: string }> = {
  // Retrievals
  retrieval_allowed: { icon: CheckCircle2, label: 'Retrieval Allowed', color: 'text-status-allow' },
  retrieval_denied: { icon: XCircle, label: 'Retrieval Denied', color: 'text-status-deny' },
  // Policies
  policy_created: { icon: Shield, label: 'Policy Created', color: 'text-status-info' },
  policy_updated: { icon: Shield, label: 'Policy Updated', color: 'text-status-info' },
  policy_activated: { icon: Shield, label: 'Policy Activated', color: 'text-status-allow' },
  policy_archived: { icon: Shield, label: 'Policy Archived', color: 'text-muted-foreground' },
  policy_deleted: { icon: Shield, label: 'Policy Deleted', color: 'text-status-deny' },
  policy_suggestion_applied: { icon: Shield, label: 'Policy Suggestion Applied', color: 'text-status-info' },
  policy_suggestions_generated: { icon: Shield, label: 'Policy Suggestions Generated', color: 'text-status-info' },
  // Connectors
  connector_added: { icon: Database, label: 'Connector Added', color: 'text-status-info' },
  connector_removed: { icon: Database, label: 'Connector Removed', color: 'text-status-deny' },
  connector_synced: { icon: Database, label: 'Connector Synced', color: 'text-status-allow' },
  connector_tested: { icon: Database, label: 'Connector Tested', color: 'text-status-info' },
  connector_updated: { icon: Database, label: 'Connector Updated', color: 'text-status-info' },
  // Resources
  resource_updated: { icon: Database, label: 'Resource Updated', color: 'text-status-info' },
  resource_metadata_updated: { icon: Database, label: 'Resource Metadata Updated', color: 'text-status-info' },
  metadata_bound: { icon: Database, label: 'Metadata Bound', color: 'text-status-info' },
  // Identity providers
  idp_added: { icon: KeyRound, label: 'IdP Added', color: 'text-status-info' },
  idp_updated: { icon: KeyRound, label: 'IdP Updated', color: 'text-status-info' },
  idp_removed: { icon: KeyRound, label: 'IdP Removed', color: 'text-status-deny' },
  idp_synced: { icon: KeyRound, label: 'IdP Synced', color: 'text-status-allow' },
  idp_sync_started: { icon: KeyRound, label: 'IdP Sync Started', color: 'text-muted-foreground' },
  idp_sync_completed: { icon: KeyRound, label: 'IdP Sync Completed', color: 'text-status-allow' },
  idp_sync_failed: { icon: KeyRound, label: 'IdP Sync Failed', color: 'text-status-warning' },
  // SCIM
  scim_token_generated: { icon: KeyRound, label: 'SCIM Token Generated', color: 'text-status-info' },
  scim_token_revoked: { icon: KeyRound, label: 'SCIM Token Revoked', color: 'text-status-deny' },
  scim_user_created: { icon: KeyRound, label: 'SCIM User Created', color: 'text-status-info' },
  scim_user_updated: { icon: KeyRound, label: 'SCIM User Updated', color: 'text-status-info' },
  scim_user_deactivated: { icon: KeyRound, label: 'SCIM User Deactivated', color: 'text-muted-foreground' },
  scim_user_reactivated: { icon: KeyRound, label: 'SCIM User Reactivated', color: 'text-status-allow' },
  scim_group_created: { icon: KeyRound, label: 'SCIM Group Created', color: 'text-status-info' },
  scim_group_updated: { icon: KeyRound, label: 'SCIM Group Updated', color: 'text-status-info' },
  scim_group_deleted: { icon: KeyRound, label: 'SCIM Group Deleted', color: 'text-status-deny' },
  // Simulator
  simulator_preview_executed: { icon: CheckCircle2, label: 'Simulator Preview', color: 'text-muted-foreground' },
  // Pipelines
  pipeline_run: { icon: ArrowRightLeft, label: 'Pipeline Run', color: 'text-status-allow' },
  pipeline_error: { icon: AlertTriangle, label: 'Pipeline Error', color: 'text-status-warning' },
  pipeline_created: { icon: ArrowRightLeft, label: 'Pipeline Created', color: 'text-status-info' },
  pipeline_updated: { icon: ArrowRightLeft, label: 'Pipeline Updated', color: 'text-status-info' },
  // User
  user_login: { icon: LogIn, label: 'User Login', color: 'text-status-info' },
  user_logout: { icon: LogIn, label: 'User Logout', color: 'text-muted-foreground' },
  settings_changed: { icon: Settings, label: 'Settings Changed', color: 'text-muted-foreground' },
  onboarding_dismissed: { icon: Settings, label: 'Onboarding Dismissed', color: 'text-muted-foreground' },
  // System-internal
  internal_credential_access: { icon: KeyRound, label: 'Credential Access', color: 'text-muted-foreground' },
  legacy_credential_migrated: { icon: KeyRound, label: 'Credential Migrated', color: 'text-muted-foreground' },
};

type FilterCategory = 'all' | 'retrieval' | 'policy' | 'connector' | 'identity' | 'pipeline' | 'user' | 'system';

const FILTER_CATEGORIES: { value: FilterCategory; label: string }[] = [
  { value: 'all', label: 'All Events' },
  { value: 'retrieval', label: 'Retrievals' },
  { value: 'policy', label: 'Policies' },
  { value: 'connector', label: 'Connectors' },
  { value: 'identity', label: 'Identity' },
  { value: 'pipeline', label: 'Pipelines' },
  { value: 'user', label: 'User Actions' },
  { value: 'system', label: 'System' },
];

const CATEGORY_PREFIXES: Record<FilterCategory, string[]> = {
  all: [],
  retrieval: ['retrieval_'],
  policy: ['policy_'],
  connector: ['connector_', 'metadata_bound', 'resource_'],
  identity: ['idp_', 'scim_'],
  pipeline: ['pipeline_'],
  user: ['user_', 'settings_', 'onboarding_'],
  system: ['internal_credential_', 'legacy_credential_', 'simulator_'],
};

function buildEventTypesParam(filter: FilterCategory): string | undefined {
  if (filter === 'all') return undefined;
  const prefixes = CATEGORY_PREFIXES[filter];
  const matching = Object.keys(EVENT_CONFIG).filter((t) =>
    prefixes.some((p) => t.startsWith(p))
  );
  return matching.length > 0 ? matching.join(',') : undefined;
}

function getRowAccent(eventType: string): string {
  if (eventType.startsWith('retrieval_denied') || eventType === 'connector_removed') return 'border-l-2 border-l-status-deny/50';
  if (eventType.startsWith('retrieval_allowed') || eventType.includes('_synced') || eventType === 'pipeline_run') return 'border-l-2 border-l-status-allow/50';
  if (eventType.includes('error') || eventType.includes('failed')) return 'border-l-2 border-l-status-warning/50';
  if (eventType.startsWith('policy_') || eventType.startsWith('connector_added') || eventType.startsWith('idp_added')) return 'border-l-2 border-l-status-info/50';
  return 'border-l-2 border-l-border';
}

export function AuditLogPage() {
  const [isExporting, setIsExporting] = useState(false);
  const [filter, setFilter] = useState<FilterCategory>('all');

  const eventTypes = buildEventTypesParam(filter);

  const { data: auditData, isLoading } = useQuery({
    queryKey: ['audit-log', filter],
    queryFn: () =>
      apiGet<AuditResponse>('/audit-log', {
        per_page: 50,
        ...(eventTypes ? { event_types: eventTypes } : {}),
      }),
  });

  const events = auditData?.data ?? [];

  async function handleExport() {
    setIsExporting(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      await apiDownloadPost(
        '/audit-log/export',
        { ...(eventTypes ? { event_types: eventTypes } : {}), format: 'json' },
        `gateco-audit-log-${today}.json`
      );
    } catch (err) {
      console.error('Audit log export failed:', err);
      toast.error('Export failed. Please try again or contact support@gateco.ai.');
    } finally {
      setIsExporting(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <PageHeader
        eyebrow="Observe"
        title="Audit Log"
        subtitle="Complete audit trail of all retrieval decisions, policy changes, connector events, and user actions."
        actions={
          <EntitlementGate feature="audit_export" mode="hide">
            <Button variant="outline" onClick={handleExport} disabled={isExporting}>
              {isExporting ? 'Exporting...' : 'Export'}
            </Button>
          </EntitlementGate>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {FILTER_CATEGORIES.map((cat) => (
          <Button
            key={cat.value}
            variant={filter === cat.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(cat.value)}
          >
            {cat.label}
          </Button>
        ))}
      </div>

      {/* Event Timeline */}
      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-16" />)}
        </div>
      ) : events.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed py-16 text-center text-muted-foreground">
          <AuditLogEmpty className="h-24 w-24 opacity-70" />
          <p className="text-sm font-medium">No audit events found</p>
          <p className="text-xs max-w-xs">
            {filter !== 'all' ? 'Try switching to "All Events" to see the full log.' : 'Audit events will appear here as your team performs actions.'}
          </p>
          {filter !== 'all' && (
            <button onClick={() => setFilter('all')} className="text-xs text-primary hover:underline mt-1">
              Clear filter
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          {events.map((evt) => {
            const config = EVENT_CONFIG[evt.event_type] ?? EVENT_CONFIG.settings_changed;
            const Icon = config.icon;
            return (
              <div
                key={evt.id}
                className={`flex items-start gap-4 rounded-lg border bg-card px-4 py-3 hover:bg-accent/50 transition-colors ${getRowAccent(evt.event_type)}`}
              >
                <div className={`mt-0.5 shrink-0 ${config.color}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-xs font-mono shrink-0">
                      {config.label}
                    </Badge>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{evt.details}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-xs font-medium">{evt.actor_name}</p>
                  <p className="text-xs text-muted-foreground">{formatRelativeDate(evt.timestamp)}</p>
                  {evt.ip_address && (
                    <p className="text-xs text-muted-foreground font-mono">{evt.ip_address}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <p className="text-xs text-muted-foreground text-center">
        Showing {events.length} of {auditData?.meta?.pagination?.total ?? 0} events
      </p>
    </div>
  );
}
